import React, { useState } from "react";
import AddIcon from "@material-ui/icons/Add";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CloseIcon from "@material-ui/icons/Close";
import "./HomeSections.css";

const IMG = (folder, file) => `${process.env.PUBLIC_URL || ""}/images/${folder}/${file}.png`;

// Same artwork in two treatments: full colour when open, calm blue when collapsed.
const MOMENTS = [
  {
    id: "painting",
    file: "01-paint-explore-create",
    tag: "Art & creativity",
    title: "Paint, explore, and create",
    text: "Open-ended art lets children experiment with color, texture, and self-expression at their own pace.",
  },
  {
    id: "blocks",
    file: "02-build-balance-discover",
    tag: "Hands-on materials",
    title: "Build, balance, and discover",
    text: "Montessori materials help children develop focus, order, and confidence through touch.",
  },
  {
    id: "reading",
    file: "03-read-listen-grow",
    tag: "Language & literacy",
    title: "Read, listen, and grow",
    text: "Story time and language work nurture curiosity and communication from an early age.",
  },
  {
    id: "nature",
    file: "04-explore-world-outside",
    tag: "Nature & care",
    title: "Explore the world outside",
    text: "Gardening and outdoor activities connect children with nature and responsible care.",
  },
  {
    id: "music",
    file: "05-rhythm-song-movement",
    tag: "Music & movement",
    title: "Rhythm, song, and movement",
    text: "Music activities support coordination, expression, and joyful group learning.",
  },
];

const CAPTURED = ["Plan the activity", "Observe progress", "Share with families"];

const pad = (n) => String(n).padStart(2, "0");

// Expanding panels: one panel is open at a time; hover, focus, tap or the arrows open another.
const ChildrenPaintingAnimation = () => {
  const [active, setActive] = useState(0);
  const [photo, setPhoto] = useState(null);
  const total = MOMENTS.length;
  const go = (next) => setActive((next + total) % total);

  const onPanelKey = (e, index) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setActive(index);
    }
  };

  return (
    <section className="hs" aria-label="Learning in action">
      <div className="hs__inner">
        <div className="lp__head">
          <header className="hs__header lp__head-text">
            <p className="hs__eyebrow">Learning in action</p>
            <h2 className="hs__title">Where every child learns by <mark className="lh-mark">doing.</mark></h2>
            <p className="hs__lead">
              A look at the moments Montestory helps teachers plan, observe, and share.
            </p>
          </header>
          <div className="lp__nav" role="group" aria-label="Browse moments">
            <span className="lp__count" aria-live="polite">
              <strong>{pad(active + 1)}</strong> / {pad(total)}
            </span>
            <button type="button" className="lp__arrow" aria-label="Previous moment" onClick={() => go(active - 1)}>
              <ChevronLeftIcon />
            </button>
            <button
              type="button"
              className="lp__arrow lp__arrow--primary"
              aria-label="Next moment"
              onClick={() => go(active + 1)}
            >
              <ChevronRightIcon />
            </button>
          </div>
        </div>

        <div className="lp">
          {MOMENTS.map(({ id, file, tag, title, text }, index) => {
            const isActive = index === active;
            return (
              <div
                key={id}
                role="button"
                tabIndex={0}
                className={`lp__panel${isActive ? " is-active" : ""}`}
                aria-expanded={isActive}
                aria-label={title}
                onMouseEnter={() => setActive(index)}
                onFocus={() => setActive(index)}
                onClick={() => setActive(index)}
                onKeyDown={(e) => onPanelKey(e, index)}
              >
                <span
                  className="lp__bg lp__bg--collapsed"
                  style={{ backgroundImage: `url("${IMG("collapsed-blue", file)}")` }}
                  aria-hidden
                />
                <span
                  className="lp__bg lp__bg--expanded"
                  style={{ backgroundImage: `url("${IMG("expanded", file)}")` }}
                  aria-hidden
                />
                <span className="lp__shade" aria-hidden />

                <span className="lp__top">
                  <span className="lp__num">{pad(index + 1)}</span>
                </span>

                <span className="lp__vtitle">{title}</span>
                <span className="lp__plus" aria-hidden>
                  <AddIcon fontSize="small" />
                </span>

                <span className="lp__body">
                  <span className="lp__tag">{tag}</span>
                  <h3 className="lp__title">{title}</h3>
                  <p className="lp__text">{text}</p>
                  <button
                    type="button"
                    className="lp__photo-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPhoto(index);
                    }}
                  >
                    <FullscreenIcon fontSize="small" /> View full photo
                  </button>
                </span>

                <span className="lp__captured" aria-hidden={!isActive}>
                  <span className="lp__captured-title">Captured in Montestory</span>
                  <ul>
                    {CAPTURED.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                </span>
              </div>
            );
          })}
        </div>

        <div className="lp__dots" role="tablist" aria-label="Moments">
          {MOMENTS.map(({ id, title }, index) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={index === active}
              aria-label={title}
              className={`lp__dot${index === active ? " is-active" : ""}`}
              onClick={() => setActive(index)}
            />
          ))}
        </div>
      </div>

      {photo !== null && (
        <div
          className="lp__lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={MOMENTS[photo].title}
          onClick={() => setPhoto(null)}
          onKeyDown={(e) => e.key === "Escape" && setPhoto(null)}
          tabIndex={-1}
          ref={(el) => el && el.focus()}
        >
          <button type="button" className="lp__lightbox-close" aria-label="Close photo" onClick={() => setPhoto(null)}>
            <CloseIcon />
          </button>
          <img
            src={IMG("expanded", MOMENTS[photo].file)}
            alt={MOMENTS[photo].title}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

export default ChildrenPaintingAnimation;
